import { FieldKey } from '../../context/field-keys.js';
import { divideBy12, multiplyBy12 } from '../../shared/conversion.js';
import { divide, multiply } from '../../shared/math.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Availability asking-rent 4-way matrix (T2.2), consolidated from
 * `projects/miq/auto-calculation/src/lib/listeners/availability-rates.binder.ts`
 * (`singleListenerToRatesFactory`, host `addListenerToTotalRent`) and its wiring in
 * `src/app/records/controls/common/listeners/availability/availability-auto-calc-listeners.ts`.
 * Expectations pinned by `availability-rates.binder.spec.ts`.
 *
 * Four fields, per-source-edge rules (one rule = one source → output edge):
 *
 *   yearly rate    (RY) = asking_rental_rate_yearly    → {@link FieldKey.AvailAskingRent}
 *   yearly amount  (AY) = asking_rental_amount_yearly  → {@link FieldKey.AvailAskingRentYearlyTotal}
 *   monthly rate   (RM) = asking_rental_rate_monthly   → {@link FieldKey.AvailAskingRentalRateMonthly}
 *   monthly amount (AM) = asking_rental_amount_monthly → {@link FieldKey.AvailTotalRentEurosPerMonth} (col 18614)
 *
 *   source RM: AM = RM×S      AY = 12×RM×S        RY = 12×RM
 *   source AM: RM = AM÷S      AY = 12×AM          RY = 12×(AM÷S)
 *   source AY: AM = AY÷12     RM = (AY÷12)÷S      RY = 12×((AY÷12)÷S)   [RY edge suppressed for AUS]
 *   source RY: AY = RY×S      AM = (RY×S)÷12      RM = RY÷12
 *   source S : composite rule (see {@link availMatrixFromSpaceSizeRule})
 *
 * Faithful semantics carried over from the listener:
 * - Intermediates stay UNROUNDED; each written output is rounded independently (`deps.round`).
 * - Guards mean NO WRITE (the legacy `updateValue` skips non-finite results; a missing source
 *   never fires) — there is no clear-on-empty in the matrix.
 * - A `''` source is ignored (legacy `filter(() => ctrlOne?.value !== '')`).
 * - The AUS suppression on the AY → RY edge (`!locale.isAus`) is `deps.isAus` here.
 *
 * Deliberate normalization (documented for the CHANGELOG): when the space size was empty, the
 * legacy listener's AY-from-RM and AM-from-RY branches multiplied/divided a locally-initialised
 * `0` and wrote a bogus `0` to the target. These rules guard on size instead and write nothing.
 */
const num = v => v == null || v === '' ? null : Number(v);
/** Mirrors the legacy `updateValue`: round, and write only finite results (never null/NaN/∞). */
function setIfFinite(ctx, key, raw, round) {
  if (!Number.isFinite(raw)) {
    return;
  }
  const rounded = round(raw, key);
  if (!Number.isFinite(rounded)) {
    return;
  }
  ctx.set(key, rounded);
}
function matrixEdgeRule(e) {
  return {
    id: `availability.asking-rent-matrix.${e.edge}`,
    inputs: e.needsSize ? [e.source, FieldKey.AvailSpaceSize] : [e.source],
    outputs: [e.output],
    formulaVersion: '1.0.0',
    run: (ctx, deps) => {
      if (e.ausSuppressed && deps?.isAus) {
        return;
      }
      const round = deps?.round ?? defaultRound;
      const source = num(ctx.get(e.source));
      if (source == null) {
        return;
      }
      let size = 0;
      if (e.needsSize) {
        const s = num(ctx.get(FieldKey.AvailSpaceSize));
        if (!s) {
          return;
        }
        size = s;
      }
      setIfFinite(ctx, e.output, e.formula(source, size), round);
    }
  };
}
// ── source: monthly rate (asking_rental_rate_monthly) ────────────────────────
/** AM = RM × S (binder `case AskingRentalRateMonthly`, TotalRentEurosPerMonth branch). */
export const availMonthlyAmountFromMonthlyRateRule = matrixEdgeRule({
  edge: 'monthly-amount-from-monthly-rate',
  source: FieldKey.AvailAskingRentalRateMonthly,
  output: FieldKey.AvailTotalRentEurosPerMonth,
  needsSize: true,
  formula: (rm, s) => multiply(rm, s)
});
/** AY = 12 × (RM × S) (same case, AskingRentYearlyTotal branch; size-guard normalized). */
export const availYearlyAmountFromMonthlyRateRule = matrixEdgeRule({
  edge: 'yearly-amount-from-monthly-rate',
  source: FieldKey.AvailAskingRentalRateMonthly,
  output: FieldKey.AvailAskingRentYearlyTotal,
  needsSize: true,
  formula: (rm, s) => multiplyBy12(multiply(rm, s))
});
/** RY = 12 × RM (same case, AskingRent branch — no size involved). */
export const availYearlyRateFromMonthlyRateRule = matrixEdgeRule({
  edge: 'yearly-rate-from-monthly-rate',
  source: FieldKey.AvailAskingRentalRateMonthly,
  output: FieldKey.AvailAskingRent,
  formula: rm => multiplyBy12(rm)
});
// ── source: monthly amount (asking_rental_amount_monthly) ────────────────────
/** RM = AM ÷ S (binder `case TotalRentEurosPerMonth`, AskingRentalRateMonthly branch). */
export const availMonthlyRateFromMonthlyAmountRule = matrixEdgeRule({
  edge: 'monthly-rate-from-monthly-amount',
  source: FieldKey.AvailTotalRentEurosPerMonth,
  output: FieldKey.AvailAskingRentalRateMonthly,
  needsSize: true,
  formula: (am, s) => divide(am, s)
});
/** AY = 12 × AM (same case, AskingRentYearlyTotal branch — unconditional on size). */
export const availYearlyAmountFromMonthlyAmountRule = matrixEdgeRule({
  edge: 'yearly-amount-from-monthly-amount',
  source: FieldKey.AvailTotalRentEurosPerMonth,
  output: FieldKey.AvailAskingRentYearlyTotal,
  formula: am => multiplyBy12(am)
});
/** RY = 12 × (AM ÷ S) (same case, AskingRent branch). */
export const availYearlyRateFromMonthlyAmountRule = matrixEdgeRule({
  edge: 'yearly-rate-from-monthly-amount',
  source: FieldKey.AvailTotalRentEurosPerMonth,
  output: FieldKey.AvailAskingRent,
  needsSize: true,
  formula: (am, s) => multiplyBy12(divide(am, s))
});
// ── source: yearly amount (asking_rental_amount_yearly) ──────────────────────
/** AM = AY ÷ 12 (binder `case AskingRentYearlyTotal`, TotalRentEurosPerMonth branch). */
export const availMonthlyAmountFromYearlyAmountRule = matrixEdgeRule({
  edge: 'monthly-amount-from-yearly-amount',
  source: FieldKey.AvailAskingRentYearlyTotal,
  output: FieldKey.AvailTotalRentEurosPerMonth,
  formula: ay => divideBy12(ay)
});
/** RM = (AY ÷ 12) ÷ S (same case, AskingRentalRateMonthly branch; exact legacy op chain). */
export const availMonthlyRateFromYearlyAmountRule = matrixEdgeRule({
  edge: 'monthly-rate-from-yearly-amount',
  source: FieldKey.AvailAskingRentYearlyTotal,
  output: FieldKey.AvailAskingRentalRateMonthly,
  needsSize: true,
  formula: (ay, s) => divide(divideBy12(ay), s)
});
/**
 * RY = 12 × ((AY ÷ 12) ÷ S) (same case, AskingRent branch). Fires only for non-AUS users —
 * the legacy `!locale.isAus` gate: an Australian yearly total is opex-inclusive, so deriving
 * an opex-exclusive yearly rate from it would be wrong (binder spec pins askingRent unchanged).
 */
export const availYearlyRateFromYearlyAmountRule = matrixEdgeRule({
  edge: 'yearly-rate-from-yearly-amount',
  source: FieldKey.AvailAskingRentYearlyTotal,
  output: FieldKey.AvailAskingRent,
  needsSize: true,
  ausSuppressed: true,
  formula: (ay, s) => multiplyBy12(divide(divideBy12(ay), s))
});
// ── source: yearly rate (asking_rental_rate_yearly) ──────────────────────────
/** AY = RY × S (binder `case AskingRent`, AskingRentYearlyTotal branch — the plain, non-Pacific total). */
export const availYearlyAmountFromYearlyRateRule = matrixEdgeRule({
  edge: 'yearly-amount-from-yearly-rate',
  source: FieldKey.AvailAskingRent,
  output: FieldKey.AvailAskingRentYearlyTotal,
  needsSize: true,
  formula: (ry, s) => multiply(ry, s)
});
/** AM = (RY × S) ÷ 12 (same case, TotalRentEurosPerMonth branch; size-guard normalized). */
export const availMonthlyAmountFromYearlyRateRule = matrixEdgeRule({
  edge: 'monthly-amount-from-yearly-rate',
  source: FieldKey.AvailAskingRent,
  output: FieldKey.AvailTotalRentEurosPerMonth,
  needsSize: true,
  formula: (ry, s) => divideBy12(multiply(ry, s))
});
/** RM = RY ÷ 12 (same case, AskingRentalRateMonthly branch — no size involved). */
export const availMonthlyRateFromYearlyRateRule = matrixEdgeRule({
  edge: 'monthly-rate-from-yearly-rate',
  source: FieldKey.AvailAskingRent,
  output: FieldKey.AvailAskingRentalRateMonthly,
  formula: ry => divideBy12(ry)
});
// ── source: space size (composite) ────────────────────────────────────────────
/**
 * Space-size-driven recompute (binder `case SpaceSize` of `singleListenerToRatesFactory`,
 * host `spaceSizeGLA$` wiring: ctrlTwo = monthly rate).
 *
 * Kept as ONE composite rule rather than per-edge rules — a deliberate choice, documented here:
 * the chained-fallback branch derives the monthly rate from the yearly rate and must then
 * (a) feed the UNROUNDED derived value into the totals and (b) suppress the RY write; splitting
 * it into edges would need that derivation state shared across rules, i.e. impure.
 *
 *   with RM present and S > 0:  AM = S×RM, AY = 12×(S×RM), RY = 12×RM
 *   with RM empty:
 *     `deps.enableChainedSizeFallbacks` off (default, MIQ 1.0): nothing — legacy matrix.
 *     on (MIQ 2.0), non-Pacific/non-AUS only, RY present: back-fill RM = RY÷12, then
 *     AM/AY as above from the unrounded derived RM; RY is NOT rewritten (no rounding drift
 *     on the user's source value). Pacific/AUS stay excluded even with the flag on — their
 *     yearly total is opex-inclusive, and a derived opex-exclusive total would contradict it.
 */
export const availMatrixFromSpaceSizeRule = {
  id: 'availability.asking-rent-matrix.from-space-size',
  inputs: [FieldKey.AvailSpaceSize, FieldKey.AvailAskingRentalRateMonthly, FieldKey.AvailAskingRent],
  outputs: [FieldKey.AvailAskingRentalRateMonthly, FieldKey.AvailTotalRentEurosPerMonth, FieldKey.AvailAskingRentYearlyTotal, FieldKey.AvailAskingRent],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const size = num(ctx.get(FieldKey.AvailSpaceSize));
    if (size == null) {
      return;
    }
    let monthlyRate = num(ctx.get(FieldKey.AvailAskingRentalRateMonthly));
    let monthlyRateDerivedFromYearly = false;
    const yearlyRate = num(ctx.get(FieldKey.AvailAskingRent));
    if (deps?.enableChainedSizeFallbacks && !deps?.isPacific && !deps?.isAus && (monthlyRate == null || Number.isNaN(monthlyRate)) && yearlyRate != null && !Number.isNaN(yearlyRate)) {
      monthlyRate = divideBy12(yearlyRate);
      setIfFinite(ctx, FieldKey.AvailAskingRentalRateMonthly, monthlyRate, round);
      monthlyRateDerivedFromYearly = true;
    }
    if (!Number.isNaN(size) && size > 0 && monthlyRate != null && !Number.isNaN(monthlyRate)) {
      const totalMonthly = multiply(size, monthlyRate);
      setIfFinite(ctx, FieldKey.AvailTotalRentEurosPerMonth, totalMonthly, round);
      setIfFinite(ctx, FieldKey.AvailAskingRentYearlyTotal, multiplyBy12(totalMonthly), round);
      if (!monthlyRateDerivedFromYearly) {
        setIfFinite(ctx, FieldKey.AvailAskingRent, multiplyBy12(monthlyRate), round);
      }
    }
  }
};
export const availabilityAskingRentMatrixRules = [availMonthlyAmountFromMonthlyRateRule, availYearlyAmountFromMonthlyRateRule, availYearlyRateFromMonthlyRateRule, availMonthlyRateFromMonthlyAmountRule, availYearlyAmountFromMonthlyAmountRule, availYearlyRateFromMonthlyAmountRule, availMonthlyAmountFromYearlyAmountRule, availMonthlyRateFromYearlyAmountRule, availYearlyRateFromYearlyAmountRule, availYearlyAmountFromYearlyRateRule, availMonthlyAmountFromYearlyRateRule, availMonthlyRateFromYearlyRateRule, availMatrixFromSpaceSizeRule];
